
import { compare, get_Sign, fromOne, op_LeftShift } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { tryPick, length, head, tail, isEmpty, sortBy, choose } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { ofSeq, toList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { comparePrimitives, arrayHash, equalArrays } from "../fable_modules/fable-library-js.5.13.0/Util.js";

/**
 * One location, and what is wrong with holding it in a memory of these widths.
 * 
 * Addresses and words are unsigned: a memory location holds a bit pattern, and the sign of the
 * number Issie displays for it is a matter of how the pattern is read, not of what is stored.
 */
export function locationProblem(addressWidth, wordWidth, address, value) {
    const locations = op_LeftShift(fromOne(), addressWidth);
    const words = op_LeftShift(fromOne(), wordWidth);
    if (get_Sign(address) < 0) {
        return `address ${address} is negative`;
    }
    else if (compare(address, locations) >= 0) {
        return `address ${address} is outside the ${locations} locations of a ${addressWidth}-bit address`;
    }
    else if (get_Sign(value) < 0) {
        return `the value ${value} at address ${address} is negative`;
    }
    else if (compare(value, words) >= 0) {
        return `the value ${value} at address ${address} does not fit in ${wordWidth}-bit words`;
    }
    else {
        return undefined;
    }
}

/**
 * What is wrong with holding `data` in a memory of these widths, said as one sentence.
 * 
 * The lowest offending address is the one named, so that the same data reported twice reports the
 * same location, and the count says how much else is wrong - naming every one of them would be a
 * list as long as the memory.
 */
export function dataProblem(addressWidth, wordWidth, data) {
    const problems = choose((tupledArg) => locationProblem(addressWidth, wordWidth, tupledArg[0], tupledArg[1]), sortBy((tuple) => tuple[0], toList(data), {
        Compare: (x, y) => (compare(x, y) | 0),
    }));
    if (!isEmpty(problems)) {
        if (isEmpty(tail(problems))) {
            return head(problems);
        }
        else {
            return `${head(problems)} (${length(tail(problems))} other location(s) do not fit either)`;
        }
    }
    else {
        return undefined;
    }
}

/**
 * What is wrong with the contents a memory is carrying, at its own widths.
 */
export function memoryProblem(mem) {
    return dataProblem(mem.AddressWidth, mem.WordWidth, mem.Data);
}

/**
 * What is wrong with holding `data` in a memory that is EVERY one of these shapes.
 * 
 * One memory component has several shapes when its widths are parameters and the sheet it is on
 * is used at several sets of values. Data has to fit all of them - it is one map, copied into
 * every instance - so the shape that fails is the one reported, named so that the message points
 * at a size the user can see in the properties pane rather than at "some instance".
 */
export function dataProblemAtWidths(widths, data) {
    return tryPick((tupledArg_1) => {
        let problem;
        const addressWidth_1 = tupledArg_1[0] | 0;
        const wordWidth_1 = tupledArg_1[1] | 0;
        const option_1 = dataProblem(addressWidth_1, wordWidth_1, data);
        if (option_1 != null) {
            return (problem = option_1, (length(List_distinct(widths, {
                Equals: equalArrays,
                GetHashCode: (x_2) => (arrayHash(x_2) | 0),
            })) === 1) ? problem : (`${problem}. This memory is ${addressWidth_1} address bits by ${wordWidth_1} data bits in at least one instance of this sheet`));
        }
        else {
            return undefined;
        }
    }, sortBy((tupledArg) => ((tupledArg[0] + tupledArg[1]) | 0), List_distinct(widths, {
        Equals: equalArrays,
        GetHashCode: (x) => (arrayHash(x) | 0),
    }), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }));
}

/**
 * What is wrong with adding this one location to a memory that is every one of these shapes.
 * Written separately from dataProblemAtWidths so that editing one location reports that location
 * rather than the lowest bad address of the whole memory.
 */
export function locationProblemAtWidths(widths, address, value) {
    return dataProblemAtWidths(widths, ofSeq([[address, value]], {
        Compare: (x, y) => (compare(x, y) | 0),
    }));
}

